import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Receipt, QrCode, Calendar, MapPin, Clock, MessageCircle, Share2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { MobileShell } from '../components/MobileShell';
import { TopBar } from '../components/TopBar';

export default function ReceiptsPage() {
  const navigate = useNavigate();
  const { api, token } = useAuth();
  const { language } = useLanguage();

  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  const fetchReceipts = useCallback(async () => {
    try {
      const response = await api().get('/receipts');
      setReceipts(response.data);
    } catch (error) {
      console.error('Error fetching receipts:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  const getCategoryName = (id) => {
    const names = {
      yacht: language === 'en' ? 'Yacht' : 'Yate',
      tour: 'Tour',
      activity: language === 'en' ? 'Activity' : 'Actividad',
      party: language === 'en' ? 'Party' : 'Fiesta',
      car_rental: language === 'en' ? 'Car Rental' : 'Renta de Auto',
      lodging: language === 'en' ? 'Lodging' : 'Hospedaje'
    };
    return names[id] || id;
  };

  const getLocationName = (id) => {
    const names = {
      playa_del_carmen: 'Playa del Carmen',
      cancun: 'Cancún',
      tulum: 'Tulum',
      quintana_roo: 'Quintana Roo',
      los_cabos: 'Los Cabos'
    };
    return names[id] || id;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-caribbean"></div>
      </div>
    );
  }

  return (
    <MobileShell>
      <TopBar
        showBrand={false}
        title={language === 'en' ? 'Receipts' : 'Recibos'}
        right={
          <button onClick={() => navigate('/dashboard')} className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted">
            <ArrowLeft size={18} />
          </button>
        }
      />
      <div className="px-4 py-4" data-testid="receipts-page">
        <h1 className="font-heading text-xl font-bold mb-4">
          {language === 'en' ? 'My Receipts & Tickets' : 'Mis Recibos y Tickets'}
        </h1>

        {receipts.length === 0 ? (
          <div className="bg-card rounded-2xl shadow-xl p-8 text-center">
            <Receipt className="mx-auto text-muted-foreground mb-4" size={48} />
            <p className="text-muted-foreground">
              {language === 'en' ? 'No receipts yet' : 'Sin recibos aún'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {receipts.map(receipt => (
              <div
                key={receipt.id}
                className="bg-card rounded-2xl shadow-xl overflow-hidden cursor-pointer hover:shadow-2xl transition-shadow"
                onClick={() => setSelectedReceipt(receipt)}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="font-heading font-bold text-lg">
                        {getCategoryName(receipt.booking?.category)}
                      </p>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <MapPin size={14} />
                        <span>{getLocationName(receipt.booking?.location)}</span>
                      </div>
                    </div>
                    <Badge className={receipt.type === 'full' ? 'bg-caribbean' : 'bg-gold text-foreground'}>
                      {receipt.type === 'full' 
                        ? (language === 'en' ? 'Full Payment' : 'Pago Completo')
                        : (language === 'en' ? 'Partial Payment' : 'Apartado')}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar size={14} />
                        <span>{receipt.booking?.date}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock size={14} />
                        <span>{receipt.booking?.time}</span>
                      </div>
                    </div>
                    <span className="font-heading text-xl font-bold text-caribbean">${receipt.amount}</span>
                  </div>
                </div>

                {/* Ticket tear line */}
                <div className="border-t-2 border-dashed border-muted" />
                
                <div className="px-6 py-3 bg-muted/30 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {new Date(receipt.created_at).toLocaleString()}
                  </span>
                  <div className="flex items-center gap-2 text-caribbean text-sm font-medium">
                    <QrCode size={16} />
                    <span>{language === 'en' ? 'View Ticket' : 'Ver Ticket'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Ticket Modal */}
        {selectedReceipt && (
          <div 
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedReceipt(null)}
          >
            <div 
              className="bg-card rounded-2xl max-w-md w-full overflow-hidden animate-scale-in"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Ticket Header */}
              <div className="bg-caribbean p-6 text-white text-center">
                <h2 className="font-heading text-2xl font-bold">BidTrip</h2>
                <p className="text-white/80 text-sm">
                  {language === 'en' ? 'Experience Ticket' : 'Ticket de Experiencia'}
                </p>
              </div>

              {/* Ticket Body */}
              <div className="p-6 space-y-4">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-1">
                    {language === 'en' ? 'Experience' : 'Experiencia'}
                  </p>
                  <p className="font-heading text-xl font-bold">
                    {getCategoryName(selectedReceipt.booking?.category)}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-muted/50 rounded-xl">
                    <p className="text-xs text-muted-foreground">{language === 'en' ? 'Location' : 'Ubicación'}</p>
                    <p className="font-semibold">{getLocationName(selectedReceipt.booking?.location)}</p>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-xl">
                    <p className="text-xs text-muted-foreground">{language === 'en' ? 'Date' : 'Fecha'}</p>
                    <p className="font-semibold">{selectedReceipt.booking?.date}</p>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-xl">
                    <p className="text-xs text-muted-foreground">{language === 'en' ? 'Time' : 'Hora'}</p>
                    <p className="font-semibold">{selectedReceipt.booking?.time}</p>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-xl">
                    <p className="text-xs text-muted-foreground">{language === 'en' ? 'Amount Paid' : 'Monto Pagado'}</p>
                    <p className="font-semibold text-caribbean">${selectedReceipt.amount}</p>
                  </div>
                </div>

                {/* QR Code */}
                <div className="flex flex-col items-center py-4">
                  <div className="p-4 bg-card border-2 border-caribbean rounded-xl">
                    <QRCodeSVG 
                      value={`BIDTRIP-${selectedReceipt.booking?.qr_code || selectedReceipt.booking?.id?.slice(0,8).toUpperCase()}`}
                      size={150}
                      level="H"
                      includeMargin={false}
                    />
                  </div>
                  <p className="mt-2 font-mono text-sm text-muted-foreground">
                    {selectedReceipt.booking?.qr_code || `BIDTRIP-${selectedReceipt.booking?.id?.slice(0,8).toUpperCase()}`}
                  </p>
                </div>

                {/* Ticket Footer */}
                <div className="border-t-2 border-dashed pt-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      {language === 'en' ? 'Receipt #' : 'Recibo #'}{selectedReceipt.id?.slice(0,8).toUpperCase()}
                    </span>
                    <Badge variant={selectedReceipt.type === 'full' ? 'default' : 'secondary'}>
                      {selectedReceipt.type === 'full' 
                        ? (language === 'en' ? 'PAID' : 'PAGADO')
                        : (language === 'en' ? 'DEPOSIT' : 'APARTADO')}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    {language === 'en' 
                      ? 'Present this QR code at your experience'
                      : 'Presenta este código QR en tu experiencia'}
                  </p>
                </div>
              </div>

              <div className="p-4 border-t flex gap-3">
                <Button
                  onClick={() => {
                    const ticketText = `🎫 *BidTrip Ticket*\n\n📍 *${getCategoryName(selectedReceipt.booking?.category)}*\n📌 ${getLocationName(selectedReceipt.booking?.location)}\n📅 ${selectedReceipt.booking?.date}\n🕐 ${selectedReceipt.booking?.time}\n💰 $${selectedReceipt.amount} USD\n\n🔑 Código: ${selectedReceipt.booking?.qr_code || `BIDTRIP-${selectedReceipt.booking?.id?.slice(0,8).toUpperCase()}`}\n\n${language === 'en' ? 'Show this ticket at your experience!' : '¡Presenta este ticket en tu experiencia!'}`;
                    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(ticketText)}`;
                    window.open(whatsappUrl, '_blank');
                  }}
                  className="flex-1 rounded-full bg-[#25D366] hover:bg-[#25D366]/90 text-white"
                >
                  <MessageCircle size={18} className="mr-2" />
                  {language === 'en' ? 'Send to WhatsApp' : 'Enviar a WhatsApp'}
                </Button>
                <Button
                  onClick={() => setSelectedReceipt(null)}
                  variant="outline"
                  className="flex-1 rounded-full"
                >
                  {language === 'en' ? 'Close' : 'Cerrar'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MobileShell>
  );
}
